import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { Flex, colors } from 'styling/GlobalStyles'
import { Button } from 'components/ui/Button/Button'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { Input } from 'components/Input/Input'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { Card, CardBody } from 'components/Card/Card'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import { addFoodEmployeeSettings } from 'features/food/foodSlice'

export const ProfilesTab = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { eligibleEmployees } = useSelector((store: RootState) => store.food)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [search, setSearch] = useState('')
  const [provisioningMode, setProvisioningMode] = useState<number>(0)

  const profiles = useMemo(() => {
    return (eligibleEmployees.data || []).filter((employee) =>
      `${employee.firstName} ${employee.lastName}`.toLowerCase().includes(search.toLowerCase()),
    )
  }, [eligibleEmployees.data, search])

  useEffect(() => {
    if (!selectedId && profiles.length > 0) {
      setSelectedId(profiles[0].id)
    }
  }, [profiles, selectedId])

  const selected = useMemo(() => profiles.find((p) => p.id === selectedId) || null, [profiles, selectedId])

  useEffect(() => {
    if (selected?.foodSettings?.provisioningMode !== undefined) {
      setProvisioningMode(selected.foodSettings.provisioningMode)
    }
  }, [selected])

  const modeOptions = [
    { label: 'Auto', value: 0 },
    { label: 'Mess Hall', value: 1 },
    { label: 'Packout', value: 2 },
    { label: 'Opt-Out', value: 3 },
  ]

  const handleSave = () => {
    if (!selected?.foodSettings) {
      return
    }
    dispatch(
      addFoodEmployeeSettings({
        id: selected.id,
        provisioningMode: {
          label: String(provisioningMode),
          value: provisioningMode,
        },
        foodSupplier: {
          label: selected.foodSettings.foodSupplier?.name || 'Supplier',
          value: selected.foodSettings.foodSupplier?.id,
        },
        maxMealsPerOperationalDay: selected.foodSettings.maxMealsPerOperationalDay,
        selectedMealTypes: (selected.foodSettings.selectedMealTypes || []).map((item) => ({
          label: item.mealType.name,
          value: item.mealType.id,
        })),
      }),
    )
    toast.success('Profile updated')
  }

  return (
    <Flex gap='1rem' width='100%' main>
      <Flex width='40%' minWidth='20rem'>
        <Container>
          <ContainerHeader>
            <TitleText weight='700'>Employee Food Registry</TitleText>
          </ContainerHeader>
          <ContainerContent gap='0.5rem'>
            <Input placeholder='Type employee name…' nomarg search value={search} onChange={(e: any) => setSearch(e.target.value)} />
            <OtherText grey>QUICK SELECT</OtherText>
            {profiles.map((p) => (
              <Card
                key={p.id}
                onClick={() => setSelectedId(p.id)}
                fullBorder={selectedId === p.id ? colors.orange : undefined}
                hoverBorder
              >
                <CardBody>
                  <BodyText weight='600'>{`${p.firstName} ${p.lastName}`}</BodyText>
                  <OtherText grey>{p.foodSettings?.foodSupplier?.name || 'No supplier'}</OtherText>
                </CardBody>
              </Card>
            ))}
          </ContainerContent>
        </Container>
      </Flex>
      <Flex width='100%'>
        <Container>
          <ContainerHeader>
            <TitleText weight='700'>Edit Profile — {selected ? `${selected.firstName} ${selected.lastName}` : 'N/A'}</TitleText>
          </ContainerHeader>
          {!selected ? (
            <ContainerContent>
              <OtherText grey>No employee found.</OtherText>
            </ContainerContent>
          ) : (
            <ContainerContent gap='1rem'>
              <Flex gap='1rem' width='100%'>
                <Flex column gap='0.25rem' width='100%'>
                  <OtherText grey>EMPLOYEE</OtherText>
                  <BodyText>{`${selected.firstName} ${selected.lastName}`}</BodyText>
                </Flex>
                <Flex column gap='0.25rem' width='100%'>
                  <OtherText grey>SUPPLIER</OtherText>
                  <BodyText>{selected.foodSettings?.foodSupplier?.name || 'N/A'}</BodyText>
                </Flex>
              </Flex>
              <Flex column gap='0.5rem'>
                <OtherText grey>DIETARY TAGS</OtherText>
                <Flex wrap gap='0.25rem'>
                  {(selected.foodSettings?.foodSupplier?.dietaryPreferenceTypes || []).map((t) => (
                    <StatusPill key={t} status='accent'>{`Tag ${t}`}</StatusPill>
                  ))}
                </Flex>
              </Flex>
              <Flex column gap='0.5rem'>
                <OtherText grey>MEAL WINDOWS</OtherText>
                <Flex wrap gap='0.25rem'>
                  {(selected.foodSettings?.selectedMealTypes || []).map((item) => (
                    <StatusPill key={item.id} status='info'>{item.mealType.name}</StatusPill>
                  ))}
                </Flex>
              </Flex>
              <HonestSelectInput
                labelText='Provisioning mode'
                options={modeOptions}
                defaultValue={provisioningMode}
                onChange={(v: any) => setProvisioningMode(Number(v.value))}
                name='provisioningMode'
              />
              <Flex>
                <Button variant='primary' onClick={handleSave}>
                  Save
                </Button>
              </Flex>
            </ContainerContent>
          )}
        </Container>
      </Flex>
    </Flex>
  )
}
